import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, MenuItem } from "@mui/material";
import {
  FaCoffee,
  FaCogs,
  FaTint,
  FaCube,
  FaMicrochip,
  FaSlidersH,
  FaListUl,
  FaPlay,
  FaPause,
  FaPeopleCarry,
  FaHashtag,
  FaCog,
  FaHistory,
  FaChartLine,
  FaClipboardList,
  FaInfoCircle,
  FaChevronDown,
  FaToggleOn,
  FaToggleOff,
  FaPlus,
  FaMinus,
  FaSitemap,
  FaRandom,
  FaBuilding,
  FaWarehouse,
  FaUndo,
} from "react-icons/fa";
import usePermission from "../../../Hooks/usePermission";
import useAuth from "../../../Hooks/useAuth";
import {
  DEVICE_ACTIVITY_ACTION_CLOSE,
  DEVICE_ACTIVITY_ACTION_OPEN,
  deviceActivityStateLabel,
} from "../../../utils/deviceActivityLabels";
import {
  shouldShowDevicePowerState,
  canToggleDeviceActivity,
  getDealerMoveActions,
  getCenterDealerMoveActions,
} from "../../../utils/deviceUtils";
import { getMainBoardVersionText } from "../../../utils/deviceCards";
import FormatDateHourString from "../../Utils/FormatDateHourString";

import turkishCoffeeMachine from "../../../Assets/icons/turkish-coffee-machine.png";
import teaFilterCoffeeMachine from "../../../Assets/icons/tea-filter-coffee-machine-icon.png";
import teaTurkishCoffeeMachine from "../../../Assets/icons/tea_coffee_machine.png";

const DEVICE_IMAGES = {
  Fincanda: turkishCoffeeMachine,
  Çayset: teaFilterCoffeeMachine,
  "Çayset-Fincanda": teaTurkishCoffeeMachine,
};

// Sticky header hücre stili — dikey scroll tablonun kendi kabuğunda olduğu
// için thead th'leri sticky çalışır; arka plan şart (satırlar altından geçer).
const TH_CLASS =
  "sticky top-0 z-10 bg-gray-100 border-b border-gray-200 px-4 py-3 text-xs font-bold text-gray-500 tracking-wide whitespace-nowrap";

/**
 * Firma > Cihazlar sekmesi — kart grid'inin yerini alan sütunlu tablo.
 * Kenar boşluksuz (full-bleed) yerleşir; başlık satırı sabittir, liste onun
 * altında kayar. İşlem sütununda iki menü butonu vardır: "Aksiyonlar"
 * (cihaz işlemleri — seçilen aksiyonun kendi drawer'ı açılır) ve
 * "Operasyonlar" (detay görünümleri — seçime göre DeviceOperationsDrawer
 * ilgili görünümle açılır).
 */
const DevicesTable = ({
  devices = [],
  // Sekmenin ait olduğu firma — cihaz objesinde firm populate yoksa geri
  // dönüş navigasyonu için fallback
  firm = null,
  isDistributingQuota = false,
  onOpenOperations,
  onOpenOperationDrawer,
  onOpenManualQuota,
  onOpenSettingsDrawer,
  // Cihaz bazında kota ver/al drawer'ını açar (device, "give"|"take").
  // Verilmezse menüde bu iki aksiyon hiç görünmez.
  onOpenQuotaDrawer,
  onToggleQuotaDistribution,
  // Firma kota dağıtımı (yalnız depo paneli) — tek cihazın menüsünden de firmanın
  // tamamı için dağıtım onayını açar; verilmezse menüde görünmez.
  onDistributeQuota,
  hideDeviceOperationButtons = false,
  // Bayi: Alt Bayiye Ver yalnız alt bayi varsa gösterilir (#247).
  hasSubDealers = false,
  // Merkezden bayi adına taşıma (#329) — (device, "toCustomer" | "takeBack");
  // verilmezse menüde görünmez.
  onOpenCenterDealerMove,
  // Bayi cihazına Kota Ver / Kota Al (#335) — (device, "give" | "take");
  // bayi cihazında Kota Dağıt'ın yerine. Verilmezse menüde görünmez.
  onOpenCenterDealerQuota,
}) => {
  const { hasPermission } = usePermission();
  // Versiyon sütunu/rozetleri cihaz detayındaki versiyon kartıyla aynı yetkiye
  // bağlı. th ve td AYNI koşulla gizlenmeli, aksi halde tablo kayar.
  const canSeeVersions = hasPermission("devices.infoVersion");
  const { auth } = useAuth();
  const navigate = useNavigate();
  const isDealerRole = auth?.role === "dealer";

  // Tek menü state'i: hangi satır, hangi tür (actions | operations)
  const [menu, setMenu] = useState({ anchorEl: null, device: null, type: null });
  const closeMenu = () => setMenu({ anchorEl: null, device: null, type: null });

  const openMenu = (event, device, type) =>
    setMenu({ anchorEl: event.currentTarget, device, type });

  const handleGoToDevice = (device) => {
    // Bayi kullanıcısı depo cihaz yoluna gidemez; rol bazlı doğru yol
    navigate(`/Anasayfa/${isDealerRole ? "Cihazlarım" : "Cihazlar"}/${device._id}`, {
      state: {
        device,
        fromPage: "Anasayfa/Firma",
        fromFirmId: device.firm?._id || firm?._id,
        fromFirmName: device.firm?.firmName || firm?.firmName,
        fromPageTab: "devices",
      },
    });
  };

  // Aksiyonlar menüsü — cihaz aksiyon izin seti.
  // "Cihaza Git" menüden kaldırıldı; navigasyon Cihaz sütunundaki linktedir.
  const getActionItems = (device) => {
    const items = [];
    if (hideDeviceOperationButtons) return items;
    // Bayinin elindeki cihaz (depo/servis/plasiyer) hep kapalı tutulur —
    // Aç/Kapat aksiyonu gizlenir, sunucu da 400 döner (2026-08-17 kararı).
    if (hasPermission("devices.toggleActivity") && canToggleDeviceActivity(auth, device, firm?._id)) {
      items.push(
        device.isActive
          ? { key: "closeDevice", label: DEVICE_ACTIVITY_ACTION_CLOSE, icon: FaPause, color: "text-red-600" }
          : { key: "openDevice", label: DEVICE_ACTIVITY_ACTION_OPEN, icon: FaPlay, color: "text-green-600" },
      );
    }
    if (hasPermission("devices.moveToPlaymaker")) {
      items.push({ key: "giveToPlaymaker", label: "Plasiyere Ver", icon: FaPeopleCarry, color: "text-amber-600" });
    }
    if (!isDealerRole && hasPermission("devices.moveToService")) {
      items.push({ key: "sendToService", label: "Servise Al", icon: FaCogs, color: "text-teal-600" });
    }
    // Bayi taşımaları (#247, sözleşme dealer-device-moves.md) — konum kuralı
    // getDealerMoveActions'ta, izin burada.
    if (isDealerRole) {
      const DEALER_MOVE_ITEMS = {
        toSubFirm: { permission: "devices.moveToFirm", label: "Alt Firmaya Ver", icon: FaBuilding, color: "text-indigo-600" },
        toSubDealer: { permission: "devices.moveToFirm", label: "Alt Bayiye Ver", icon: FaSitemap, color: "text-blue-600" },
        takeBack: { permission: "devices.moveToStorage", label: "Bayiye Geri Al", icon: FaUndo, color: "text-purple-600" },
        toStorage: { permission: "devices.moveToStorage", label: "Depoya Al", icon: FaWarehouse, color: "text-purple-600" },
        toService: { permission: "devices.moveToService", label: "Servise Al", icon: FaCogs, color: "text-teal-600" },
      };
      getDealerMoveActions(auth, device, { hasSubDealers, fallbackFirmId: firm?._id }).forEach((key) => {
        const item = DEALER_MOVE_ITEMS[key];
        if (hasPermission(item.permission)) items.push({ key, ...item });
      });
    }
    // Merkezden bayi adına taşıma (#329) — cihazın firmasına göre.
    if (onOpenCenterDealerMove) {
      const CENTER_DEALER_MOVE_ITEMS = {
        toCustomer: { key: "centerToCustomer", permission: "devices.moveToFirm", label: "Bayi Müşterisine Ver", icon: FaBuilding, color: "text-indigo-600" },
        takeBack: { key: "centerTakeBack", permission: "devices.moveToStorage", label: "Bayiye Geri Al", icon: FaUndo, color: "text-purple-600" },
      };
      getCenterDealerMoveActions(auth, device).forEach((mode) => {
        const item = CENTER_DEALER_MOVE_ITEMS[mode];
        if (hasPermission(item.permission)) items.push(item);
      });
    }
    // Manuel kota yalnız merkezde — bayi DevicesTab onOpenManualQuota vermez (#178).
    if (
      onOpenManualQuota &&
      hasPermission("devices.quota") &&
      device?.productInfo?.productInfo?.length > 0 &&
      !device?.serialNo?.startsWith("X460PLUS")
    ) {
      items.push({ key: "manualQuota", label: "Manuel Kota Yükle", icon: FaHashtag, color: "text-violet-600" });
    }
    // Firma bakiyesi ↔ cihaz kota transferi (şu an yalnız bayi panelinde;
    // izinler bayi kataloğunda tanımlı). Cihazın ürünü yoksa anlamsız.
    if (onOpenQuotaDrawer && device?.productInfo?.productInfo?.length > 0) {
      if (hasPermission("devices.giveQuota")) {
        items.push({ key: "giveQuota", label: "Kota Ver", icon: FaPlus, color: "text-emerald-600" });
      }
      if (hasPermission("devices.takeBackQuota")) {
        items.push({ key: "takeBackQuota", label: "Kota Al", icon: FaMinus, color: "text-amber-600" });
      }
    }
    // Bayideki cihaz her zaman kapalı — Kota Dağıt yerine Kota Ver / Kota Al (#335)
    const isDealerHeldForCenter = !isDealerRole && device?.firm?.firmType === "dealer";
    if (onDistributeQuota && !isDealerHeldForCenter) {
      items.push({ key: "distributeQuota", label: "Kota Dağıt", icon: FaRandom, color: "text-indigo-600" });
    }
    if (onOpenCenterDealerQuota && isDealerHeldForCenter && hasPermission("firms.distributeQuota")) {
      items.push({ key: "centerGiveQuota", label: "Kota Ver", icon: FaPlus, color: "text-emerald-600" });
      items.push({ key: "centerTakeQuota", label: "Kota Al", icon: FaMinus, color: "text-amber-600" });
    }
    if (hasPermission("devices.settings")) {
      items.push({ key: "openSettings", label: "Ayarlar", icon: FaCog, color: "text-slate-600" });
    }
    return items;
  };

  // Operasyonlar menüsü — cihaz operasyon izin seti;
  // seçim DeviceOperationsDrawer'ı ilgili görünümle açar
  const getOperationItems = () => {
    const items = [];
    // Son İşlem bayide YOK (#180) — eski şablonda izin kalmış olabilir.
    if (!isDealerRole && hasPermission("devices.lastOperation")) {
      items.push({ key: "lastOperation", label: "Son İşlem", icon: FaHistory, color: "text-violet-600" });
    }
    if (hasPermission("devices.stats")) {
      items.push({ key: "openPerformance", label: "Performans", icon: FaChartLine, color: "text-indigo-600" });
    }
    if (hasPermission("devices.reportSettings")) {
      items.push({ key: "settingLogs", label: "Ayar Değişiklikleri", icon: FaClipboardList, color: "text-orange-600" });
    }
    if (hasPermission("devices.reportOperation")) {
      items.push({ key: "operationLogs", label: "Operasyonlar", icon: FaListUl, color: "text-cyan-600" });
    }
    if (hasPermission("devices.reportInfo")) {
      items.push({ key: "infoLogs", label: "Bilgi Değişiklikleri", icon: FaInfoCircle, color: "text-pink-600" });
    }
    return items;
  };

  const handleActionSelect = (device, key) => {
    closeMenu();
    switch (key) {
      case "closeDevice":
        onOpenOperationDrawer && onOpenOperationDrawer(device, "AktiflikKapat");
        break;
      case "openDevice":
        onOpenOperationDrawer && onOpenOperationDrawer(device, "AktiflikAc");
        break;
      case "giveToPlaymaker":
        onOpenOperationDrawer && onOpenOperationDrawer(device, "Plasiyer");
        break;
      case "sendToService":
      case "toService":
        onOpenOperationDrawer && onOpenOperationDrawer(device, "Servis");
        break;
      case "toSubFirm":
        onOpenOperationDrawer && onOpenOperationDrawer(device, "Firma", { firmTarget: "customer" });
        break;
      case "toSubDealer":
        onOpenOperationDrawer && onOpenOperationDrawer(device, "Firma", { firmTarget: "dealer" });
        break;
      case "takeBack":
      case "toStorage":
        onOpenOperationDrawer && onOpenOperationDrawer(device, "Depo");
        break;
      case "manualQuota":
        onOpenManualQuota && onOpenManualQuota(device);
        break;
      case "giveQuota":
        onOpenQuotaDrawer && onOpenQuotaDrawer(device, "give");
        break;
      case "takeBackQuota":
        onOpenQuotaDrawer && onOpenQuotaDrawer(device, "take");
        break;
      case "distributeQuota":
        onDistributeQuota && onDistributeQuota(device);
        break;
      case "centerToCustomer":
        onOpenCenterDealerMove && onOpenCenterDealerMove(device, "toCustomer");
        break;
      case "centerTakeBack":
        onOpenCenterDealerMove && onOpenCenterDealerMove(device, "takeBack");
        break;
      case "centerGiveQuota":
        onOpenCenterDealerQuota && onOpenCenterDealerQuota(device, "give");
        break;
      case "centerTakeQuota":
        onOpenCenterDealerQuota && onOpenCenterDealerQuota(device, "take");
        break;
      case "openSettings":
        onOpenSettingsDrawer && onOpenSettingsDrawer(device);
        break;
      default:
        break;
    }
  };

  const handleOperationSelect = (device, key) => {
    closeMenu();
    onOpenOperations && onOpenOperations(device, key);
  };

  const menuItems =
    menu.type === "actions"
      ? getActionItems(menu.device || {})
      : menu.type === "operations"
        ? getOperationItems()
        : [];

  return (
    <div className="h-full overflow-auto bg-white border-t border-gray-200">
      {/* Mobil kart görünümü — dar ekranda tablo yerine dikey kartlar */}
      <div className="sm:hidden divide-y divide-gray-100">
        {devices.map((device, index) => {
          const isConnected = device.deviceInfo?.connection?.status;
          const showPowerState = shouldShowDevicePowerState(auth, device, firm?._id);
          const image = DEVICE_IMAGES[device.deviceInfo?.deviceName];
          const products = device.productInfo?.productInfo || [];
          const deviceFirmName = device.firm?.firmName || firm?.firmName || "";
          const isSubFirmDevice =
            device.isSubFirmDevice ??
            (!!device.firm?._id &&
              !!firm?._id &&
              String(device.firm._id) !== String(firm._id));
          return (
            <div
              key={device._id || index}
              className={`px-3.5 py-3 space-y-2 ${isDistributingQuota ? "opacity-50 pointer-events-none" : ""}`}
            >
              {/* Üst: cihaz linki + durum rozetleri */}
              <div className="flex items-start justify-between gap-2">
                {hasPermission("devices.info") ? (
                  <button
                    onClick={() => handleGoToDevice(device)}
                    className="group flex items-center gap-2.5 min-w-0 text-left"
                    title="Cihaza git"
                  >
                    {image && (
                      <div className="w-10 h-10 shrink-0 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden">
                        <img src={image} alt={device.deviceInfo?.deviceName} className="max-w-full max-h-full object-contain p-0.5" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate group-active:text-indigo-700">
                        {device.customName || device.deviceName}
                      </p>
                      <p className="text-xs text-gray-400 font-mono truncate">{device.serialNo}</p>
                    </div>
                  </button>
                ) : (
                  <div className="flex items-center gap-2.5 min-w-0">
                    {image && (
                      <div className="w-10 h-10 shrink-0 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden">
                        <img src={image} alt={device.deviceInfo?.deviceName} className="max-w-full max-h-full object-contain p-0.5" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">{device.customName || device.deviceName}</p>
                      <p className="text-xs text-gray-400 font-mono truncate">{device.serialNo}</p>
                    </div>
                  </div>
                )}
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {showPowerState && (
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold whitespace-nowrap ${device.isActive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${device.isActive ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
                      {deviceActivityStateLabel(device.isActive)}
                    </span>
                  )}
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold whitespace-nowrap ${isConnected ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${isConnected ? "bg-emerald-500 animate-pulse" : "bg-red-500"}`} />
                    {isConnected ? "Bağlı" : "Bağlı Değil"}
                  </span>
                </div>
              </div>

              {/* Kota satırları (switch dahil) */}
              {products.length > 0 && (
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {products.map((item, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1.5 text-sm whitespace-nowrap">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onToggleQuotaDistribution && hasPermission("devices.productAutoDistributeQuota"))
                            onToggleQuotaDistribution(device, item);
                        }}
                        disabled={!hasPermission("devices.productAutoDistributeQuota")}
                        className={`inline-flex items-center shrink-0 ${item.quotaDistribution ? "text-emerald-500" : "text-gray-300"}`}
                      >
                        {item.quotaDistribution ? <FaToggleOn className="w-4 h-4" /> : <FaToggleOff className="w-4 h-4" />}
                      </button>
                      <span className="text-gray-600">{item.productName}</span>
                      <span className="font-Bold text-indigo-600 tabular-nums">{(item.quota ?? 0).toLocaleString("tr-TR")}</span>
                      {/* CEVRIMDISI IADE MAHSUBU BEKLIYOR — cihaz cevrimdisiyken
                          kotasi firmaya iade edildi; baglandiginda aradaki
                          fark firmadan dusulecek. Cihaz detayi urun kartindaki
                          rozetin bu tablodaki
                          karsiligi (ayni esik, ayni metin). */}
                      {Number(item.pendingReturnedQuota) > 0 && (
                        <span className="text-[11px] text-rose-600 font-semibold animate-pulse">
                          Mahsup bekliyor: {Number(item.pendingReturnedQuota).toLocaleString("tr-TR")}
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              )}

              {/* Parça + versiyon + son bağlantı — kompakt rozetler */}
              <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-700 font-semibold border border-slate-200 whitespace-nowrap">
                  <FaCoffee className="w-2.5 h-2.5" /> {device.deviceInfo?.cupNumber ?? 0} göz
                </span>
                {canSeeVersions && (
                  <>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-indigo-50 text-indigo-700 font-semibold whitespace-nowrap">
                      <FaCube className="w-2.5 h-2.5" /> {device.deviceInfo?.appVersion || "-"}
                    </span>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700 font-semibold whitespace-nowrap">
                      <FaMicrochip className="w-2.5 h-2.5" /> {getMainBoardVersionText(device.deviceInfo) || "-"}
                    </span>
                  </>
                )}
                <span className="text-gray-400 whitespace-nowrap">
                  Son: {FormatDateHourString(device.lastConnectionDate)}
                </span>
                {/* Alt firma cihazı: hangi firmaya ait olduğu rozette */}
                {isSubFirmDevice && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-violet-50 text-violet-700 font-SemiBold border border-violet-200 max-w-full">
                    <FaSitemap className="w-2.5 h-2.5 shrink-0" />
                    <span className="truncate">{deviceFirmName}</span>
                  </span>
                )}
              </div>

              {/* Aksiyonlar + Operasyonlar butonları yan yana */}
              <div className="flex gap-2">
                <button
                  onClick={(e) => openMenu(e, device, "actions")}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-slate-50 text-slate-700 border border-slate-200 active:bg-slate-100"
                >
                  <FaSlidersH className="w-3.5 h-3.5" /> Aksiyonlar
                </button>
                <button
                  onClick={(e) => openMenu(e, device, "operations")}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-indigo-50 text-indigo-600 border border-indigo-100 active:bg-indigo-100"
                >
                  <FaListUl className="w-3.5 h-3.5" /> Operasyonlar
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <table className="hidden sm:table w-full min-w-[1024px] text-sm">
        <thead>
          <tr>
            <th className={`${TH_CLASS} text-left`}>CİHAZ</th>
            <th className={`${TH_CLASS} text-left`}>KOTA</th>
            <th className={`${TH_CLASS} text-left`}>DURUM</th>
            <th className={`${TH_CLASS} text-left`}>FİRMA</th>
            <th className={`${TH_CLASS} text-left`}>PARÇALAR</th>
            {canSeeVersions && <th className={`${TH_CLASS} text-left`}>VERSİYON</th>}
            <th className={`${TH_CLASS} text-right`}>İŞLEM</th>
          </tr>
        </thead>
        <tbody>
          {devices.map((device, index) => {
            const isConnected = device.deviceInfo?.connection?.status;
            // Sunucu alt firma cihazlarını da döndürüyor; bayrak yoksa (eski
            // sürüm/başka çağrı) firma id karşılaştırmasına düşülür.
            const deviceFirmName = device.firm?.firmName || firm?.firmName || "";
            const isSubFirmDevice =
              device.isSubFirmDevice ??
              (!!device.firm?._id &&
                !!firm?._id &&
                String(device.firm._id) !== String(firm._id));
            const showPowerState = shouldShowDevicePowerState(auth, device, firm?._id);
            const image = DEVICE_IMAGES[device.deviceInfo?.deviceName];
            const products = device.productInfo?.productInfo || [];
            return (
              <tr
                key={device._id || index}
                className={`bg-white even:bg-gray-50/70 border-b border-gray-100 last:border-b-0 hover:bg-gray-100/70 transition-colors ${
                  isDistributingQuota ? "opacity-50 pointer-events-none" : ""
                }`}
              >
                {/* Cihaz — ikon + model + seri no tek link; herhangi birine
                    hover'da üçü birden link görünümüne geçer (group-hover),
                    tıklayınca cihaz detayına gider (devices.info izniyle) */}
                <td className="px-4 py-3 align-middle">
                  {hasPermission("devices.info") ? (
                    <button
                      onClick={() => handleGoToDevice(device)}
                      className="group flex items-center gap-3 min-w-0 w-fit max-w-full text-left"
                      title="Cihaza git"
                    >
                      {image && (
                        <div className="w-11 h-11 shrink-0 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden border border-transparent transition-colors group-hover:bg-indigo-50 group-hover:border-indigo-300">
                          <img
                            src={image}
                            alt={device.deviceInfo?.deviceName}
                            className="max-w-full max-h-full object-contain p-0.5"
                          />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-900 truncate transition-colors group-hover:text-indigo-700 group-hover:underline underline-offset-2 decoration-indigo-400">
                          {device.customName || device.deviceName}
                          {device.customName && (
                            <span className="ml-1 text-xs text-gray-400 font-normal group-hover:text-indigo-400">
                              ({device.deviceName})
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-400 font-mono truncate transition-colors group-hover:text-indigo-500 group-hover:underline underline-offset-2 decoration-indigo-300">
                          {device.serialNo}
                        </p>
                        {/* Son bağlantı seri no'nun altında — kendi sütunu
                            kaldırıldı, o sütunda artık cihazın firması yazıyor */}
                        <p className="text-[11px] text-gray-400 truncate">
                          Son: {FormatDateHourString(device.lastConnectionDate)}
                        </p>
                      </div>
                    </button>
                  ) : (
                    <div className="flex items-center gap-3 min-w-0">
                      {image && (
                        <div className="w-11 h-11 shrink-0 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden">
                          <img
                            src={image}
                            alt={device.deviceInfo?.deviceName}
                            className="max-w-full max-h-full object-contain p-0.5"
                          />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-900 truncate">
                          {device.customName || device.deviceName}
                          {device.customName && (
                            <span className="ml-1 text-xs text-gray-400 font-normal">
                              ({device.deviceName})
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-400 font-mono truncate">{device.serialNo}</p>
                        <p className="text-[11px] text-gray-400 truncate">
                          Son: {FormatDateHourString(device.lastConnectionDate)}
                        </p>
                      </div>
                    </div>
                  )}
                </td>

                {/* Kota — ürün başına satır; dağıtım aç/kapa switch'i
                    (karttaki toggle davranışının aynısı, izinle korunur) */}
                <td className="px-4 py-3 align-middle">
                  {products.length === 0 ? (
                    <span className="text-xs text-gray-300">-</span>
                  ) : (
                    <div className="flex flex-col gap-1">
                      {products.map((item, idx) => (
                        <span key={idx} className="inline-flex items-center gap-2 text-sm whitespace-nowrap">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (
                                onToggleQuotaDistribution &&
                                hasPermission("devices.productAutoDistributeQuota")
                              )
                                onToggleQuotaDistribution(device, item);
                            }}
                            disabled={!hasPermission("devices.productAutoDistributeQuota")}
                            className={`inline-flex items-center shrink-0 transition-colors disabled:cursor-not-allowed ${
                              item.quotaDistribution
                                ? "text-emerald-500 hover:text-emerald-600"
                                : "text-gray-300 hover:text-gray-400"
                            }`}
                            title={item.quotaDistribution ? "Kota almaya açık" : "Kota almaya kapalı"}
                          >
                            {item.quotaDistribution ? (
                              <FaToggleOn className="w-5 h-5" />
                            ) : (
                              <FaToggleOff className="w-5 h-5" />
                            )}
                          </button>
                          <span className="text-gray-600">{item.productName}</span>
                          <span className="font-Bold text-indigo-600 tabular-nums">
                            {(item.quota ?? 0).toLocaleString("tr-TR")}
                          </span>
                          {/* CEVRIMDISI IADE MAHSUBU BEKLIYOR — cihaz cevrimdisiyken
                          kotasi firmaya iade edildi; baglandiginda aradaki
                          fark firmadan dusulecek. Cihaz detayi urun kartindaki
                          rozetin bu tablodaki
                          karsiligi (ayni esik, ayni metin). */}
                          {Number(item.pendingReturnedQuota) > 0 && (
                            <span className="text-[11px] text-rose-600 font-semibold animate-pulse">
                              Mahsup bekliyor:{" "}
                              {Number(item.pendingReturnedQuota).toLocaleString("tr-TR")}
                            </span>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </td>

                {/* Durum — Kota Almaya Açık yeşil (nabızlı dot), Kapalı kırmızı;
                    Bağlı yeşil (nabızlı dot), Bağlı Değil kırmızı.
                    Bayi panelinde bayinin kendi firmasındaki cihazlarda
                    Açık/Kapalı basılmaz (bkz. shouldShowDevicePowerState). */}
                <td className="px-4 py-3 align-middle">
                  <div className="flex flex-col gap-1 w-fit">
                    {showPowerState && (
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-semibold whitespace-nowrap ${
                          device.isActive ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
                        }`}
                      >
                        <span
                          className={`w-2 h-2 rounded-full ${
                            device.isActive ? "bg-emerald-500 animate-pulse" : "bg-red-500"
                          }`}
                        />
                        {deviceActivityStateLabel(device.isActive)}
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md text-xs font-semibold whitespace-nowrap ${
                        isConnected ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
                      }`}
                    >
                      <span
                        className={`w-2 h-2 rounded-full ${
                          isConnected ? "bg-emerald-500 animate-pulse" : "bg-red-500"
                        }`}
                      />
                      {isConnected ? "Bağlı" : "Bağlı Değil"}
                    </span>
                  </div>
                </td>

                {/* Firma — liste artık ALT firmaların cihazlarını da içeriyor;
                    hangi firmaya ait olduğu burada yazar. Alt firma ise rozetle
                    ayrılır, kendi firmasının cihazı sade gösterilir. */}
                <td className="px-4 py-3 align-middle">
                  {isSubFirmDevice ? (
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <span className="inline-flex items-center gap-1.5 w-fit text-xs px-2 py-1 rounded-md bg-violet-50 text-violet-700 font-SemiBold border border-violet-200 whitespace-nowrap">
                        <FaSitemap className="w-3 h-3 shrink-0" /> Alt Firma
                      </span>
                      <span
                        className="text-sm text-gray-700 truncate"
                        title={deviceFirmName}
                      >
                        {deviceFirmName}
                      </span>
                    </div>
                  ) : (
                    <span
                      className="text-sm text-gray-600 truncate block"
                      title={deviceFirmName}
                    >
                      {deviceFirmName || "-"}
                    </span>
                  )}
                </td>

                {/* Parçalar */}
                <td className="px-4 py-3 align-middle">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-slate-100 text-slate-700 font-semibold border border-slate-200 whitespace-nowrap">
                      <FaCoffee className="w-3 h-3 shrink-0" /> {device.deviceInfo?.cupNumber ?? 0} göz
                    </span>
                    {device.componentSummary?.grinderStatus && device.componentSummary.grinderStatus !== "-" && (
                      <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-amber-50 text-amber-700 font-semibold border border-amber-200 whitespace-nowrap">
                        <FaCogs className="w-3 h-3 shrink-0" /> Değirmen
                      </span>
                    )}
                    {device.componentSummary?.waterModeStatus && device.componentSummary.waterModeStatus !== "-" && (
                      <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-blue-50 text-blue-700 font-semibold border border-blue-200 whitespace-nowrap">
                        <FaTint className="w-3 h-3 shrink-0" /> {device.componentSummary.waterModeStatus}
                      </span>
                    )}
                  </div>
                </td>

                {/* Versiyon */}
                {canSeeVersions && (
                <td className="px-4 py-3 align-middle">
                  <div className="flex flex-col gap-1 w-fit">
                    <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-indigo-50 text-indigo-700 font-semibold whitespace-nowrap">
                      <FaCube className="w-3 h-3 shrink-0" /> Ekran: {device.deviceInfo?.appVersion || "-"}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 font-semibold whitespace-nowrap">
                      <FaMicrochip className="w-3 h-3 shrink-0" /> Kart: {getMainBoardVersionText(device.deviceInfo) || "-"}
                    </span>
                  </div>
                </td>
                )}

                {/* İşlem — iki menü butonu alt alta; seçime göre ilgili
                    drawer açılır */}
                <td className="px-4 py-3 align-middle">
                  <div className="flex flex-col items-stretch justify-center gap-1.5 w-fit ml-auto">
                    <button
                      onClick={(e) => openMenu(e, device, "actions")}
                      className="inline-flex items-center justify-between gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-slate-50 text-slate-700 border border-slate-200 hover:bg-slate-100 transition-colors whitespace-nowrap"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <FaSlidersH className="w-3.5 h-3.5 shrink-0" />
                        Aksiyonlar
                      </span>
                      <FaChevronDown className="w-2.5 h-2.5 shrink-0 opacity-60" />
                    </button>
                    <button
                      onClick={(e) => openMenu(e, device, "operations")}
                      className="inline-flex items-center justify-between gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-indigo-50 text-indigo-600 border border-indigo-100 hover:bg-indigo-100 transition-colors whitespace-nowrap"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <FaListUl className="w-3.5 h-3.5 shrink-0" />
                        Operasyonlar
                      </span>
                      <FaChevronDown className="w-2.5 h-2.5 shrink-0 opacity-60" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Menü — portal'da açılır (scroll kabında kırpılmaz) */}
      <Menu
        anchorEl={menu.anchorEl}
        open={!!menu.anchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              minWidth: 200,
              mt: 0.5,
            },
          },
        }}
      >
        {menuItems.map((item) => (
          <MenuItem
            key={item.key}
            onClick={() =>
              menu.type === "actions"
                ? handleActionSelect(menu.device, item.key)
                : handleOperationSelect(menu.device, item.key)
            }
            sx={{ fontSize: "13px", fontWeight: 500, gap: 1.25, py: 1 }}
          >
            <item.icon className={`w-3.5 h-3.5 shrink-0 ${item.color}`} />
            {item.label}
          </MenuItem>
        ))}
        {menuItems.length === 0 && (
          <MenuItem disabled sx={{ fontSize: "13px" }}>
            Yetkili işlem yok
          </MenuItem>
        )}
      </Menu>
    </div>
  );
};

export default DevicesTable;
